/**
 * `custom()` — the escape hatch (SPEC.md §5.2), and its replay-to-resume machinery
 * (SPEC.md §4.3).
 *
 * A handful of official cards print text the combinator vocabulary cannot express. For those
 * — and **only** those — a card may name a **synchronous generator** registered under an id,
 * and the interpreter drives it. Two properties are non-negotiable, and everything in this
 * module exists to keep them:
 *
 * 1. **Every player decision is a yielded `ChoiceRequest` with an explicit legal-option
 *    list.** Opaque *effects* are tolerable; opaque *choices* are not, because the AI, the UI
 *    and the action log all drive the game through one option list.
 * 2. **A state mid-`custom()` clones, serializes and searches like any other.** A live JS
 *    generator cannot be cloned, so none is ever stored: the frame keeps the generator's
 *    **id** plus a **transcript** of everything that has already happened, all plain data,
 *    and the generator is re-created and fast-forwarded from that transcript every time the
 *    interpreter needs it.
 *
 * ## The authoring contract
 *
 * ```ts
 * registerCustom('look-and-take', function* (ctx) {
 *   const top = yield* readPool({ zone: 'deck', controller: 'you' });
 *   if (top.length === 0) return;
 *   const picked = yield* chooseCard({ selector: { zone: 'deck', controller: 'you' } });
 *   if (picked === null) return;
 *   yield* runEffect(dsl.draw(1));
 * });
 * ```
 *
 * A generator **never touches `GameState`**. It only ever `yield`s, and there are exactly
 * three things to yield (all through the typed `yield*` helpers below):
 *
 * | Yield | Meaning | Reply |
 * |---|---|---|
 * | `choose` | pause on a `ChoiceRequest` with an explicit option list | the chosen token |
 * | `run` | hand one IR `Effect` to the interpreter to execute | — |
 * | `read` | ask one question about the state | a **fact** (number/boolean/id list) |
 *
 * That is what makes the generator a **pure function of its transcript**: it observes the
 * game only through recorded replies, so re-running it with the same transcript necessarily
 * reaches the same point. The lint rules in `src/lint/custom-lint.ts` enforce the rest of the
 * contract statically (no `async`, no ambient randomness, no direct state writes).
 *
 * ## Replay-to-resume
 *
 * The interpreter never keeps a suspended generator — not even within a single `apply`. Every
 * time an effect frame reaches its `custom` node it:
 *
 * 1. re-creates the generator from the registry (`id` → the registered function);
 * 2. walks it forward, feeding the recorded transcript entry for each yield in order —
 *    **without** re-running effects and **without** re-reading the state;
 * 3. carries out the first yield the transcript does not cover, appending to the transcript.
 *
 * So resuming a clone is not a special path: it is *the* path, exercised on every step, which
 * is why a clone taken at a mid-ability pause reproduces the exact pause point. A yield that
 * disagrees with its recorded entry means the generator is not deterministic, and throws
 * (`CustomAbilityError`) rather than silently diverging.
 *
 * ## Identity-free by construction
 *
 * The transcript rides inside `GameState.stack`, and frames ride into `playerView` unredacted
 * (ticket 05's constraint). So nothing in a transcript may name a printed card *anyone is
 * still entitled not to know*:
 *
 * - a recorded **fact** is a number, a boolean, a list of instance ids — never a `defId` —
 *   or, for the one read that is *about* identity, a list of card-name strings: `readNames`
 *   refuses any card whose identity is not already public to every player, so a recorded
 *   name never tells a viewer anything the table was not told first;
 * - a choice **token** is `i<instanceId>` (a public instance), the reserved `none`, a
 *   generator-supplied dense token, or a `n.…` **declared card name** (`nameToken`) — a
 *   public announcement, so it carries exactly what the player said out loud; a `d<defId>`
 *   token is rejected outright.
 */
import { cardNamesOf, declarableNames, requireDefinition } from '../cards.ts';
import type { ChoiceOption, ChoiceRequest } from '../choices.ts';
import { option } from '../choices.ts';
import type { InstanceId, PlayerIndex } from '../ids.ts';
import type { Frame, GameState } from '../state.ts';
import { allPlayersMask, instanceAt } from '../state.ts';
import { emit } from '../zones.ts';
import type { Ability, Amount, CardRef, Condition, Effect, Selector } from './ir.ts';
import { canBeChosenBy } from './keywords.ts';
import type { AbilityContext } from './selectors.ts';
import { chooserOf, evalAmount, evalCondition, resolveCardRefIn, selectorPool } from './selectors.ts';

/** Anything wrong with a `custom()` ability — registration, determinism or its contract. */
export class CustomAbilityError extends Error {}

/** The `effect` frame, narrowed. Same type as the interpreter's `EffectFrame`. */
type EffectFrame = Extract<Frame, { kind: 'effect' }>;

/**
 * Guard against a generator that loops forever without yielding a decision. A printed card
 * needs a handful of steps; hundreds means a bug.
 */
const MAX_CUSTOM_STEPS = 512;

// ---------------------------------------------------------------------------
// The yield protocol
// ---------------------------------------------------------------------------

/** What a generator is told about its own ability. Identity-free: ids and seats only. */
export interface CustomContext {
  /** The card whose ability this is. */
  readonly source: InstanceId;
  /** Whose ability it is — the chooser for every decision it raises. */
  readonly controller: PlayerIndex;
  /** The card the triggering event was about, if any. */
  readonly subject: InstanceId | null;
}

/** One explicit option of a generator-supplied option list. */
export interface CustomOptionSpec {
  /** Dense and unique within the choice; becomes the `custom:<token>` action key. */
  readonly token: string;
  /** Prompt text for the UI. Never load-bearing. */
  readonly label?: string;
}

/**
 * A decision, as the generator describes it. Both shapes carry an **explicit legal-option
 * list** by the time they reach `legalActions` — the `cards` shape by evaluating its selector
 * against the state, exactly as an ordinary `target` node does.
 */
export type CustomChoiceSpec =
  /** "Choose a card …" — the pool is the selector's, minus Ward (CR §8.15). */
  | {
      readonly kind: 'cards';
      readonly selector: Selector;
      /** Bind the pick under this id, so later IR can name it with `bound(id)`. */
      readonly as?: string;
      /** Offer `none` as well. Always offered when the pool is empty. */
      readonly optional?: boolean;
      readonly label?: string;
      /** Who picks. Default the ability's controller — `opponent` is "each opponent chooses". */
      readonly chooser?: 'controller' | 'opponent';
    }
  /** A generator-supplied list — yes/no, modes, "how many", anything not a card. */
  | {
      readonly kind: 'options';
      readonly options: readonly CustomOptionSpec[];
      readonly label?: string;
      readonly chooser?: 'controller' | 'opponent';
    }
  /**
   * "Name a card" — the chooser **declares** a card identity as a fact, before anything is
   * revealed, with no pool in front of them. The options are every name the catalog can
   * legally answer to (`declarableNames`): real LorcanaJSON **base names**, never free text
   * and never a version name — which is how the official clarification ("you don't need to
   * declare the version name") is satisfied by construction. Each option's token is the
   * name itself, reversibly encoded (`nameToken`), so the declaration is public data the
   * generator can compare against a later reveal.
   */
  | {
      readonly kind: 'name';
      readonly label?: string;
      readonly chooser?: 'controller' | 'opponent';
    };

/** One question a generator may ask about the state. Every answer is a recordable fact. */
export type CustomRead =
  /** How many cards satisfy a selector. */
  | { readonly kind: 'count'; readonly of: Selector }
  /** Any IR `Amount` — "their {S}", "damage on that card", "for each …". */
  | { readonly kind: 'amount'; readonly amount: Amount }
  /** Any IR `Condition` — the general "if" the generator branches on. */
  | { readonly kind: 'test'; readonly condition: Condition }
  /** The instance ids satisfying a selector, in the engine's canonical pool order. */
  | { readonly kind: 'pool'; readonly of: Selector }
  /**
   * The printed name(s) of the card a reference names — `cardNamesOf`: the base name plus
   * each name of a multi-name card. **Public identities only**: the card must already be
   * known to every player (in a public zone, or revealed), because the answer is recorded on
   * a frame that rides into `playerView`. An unresolvable reference reads `[]` (CR §1.2.3).
   */
  | { readonly kind: 'names'; readonly of: CardRef };

/** One thing a generator hands back to the interpreter. */
export type CustomYield =
  | { readonly kind: 'choose'; readonly spec: CustomChoiceSpec }
  | { readonly kind: 'run'; readonly effect: Effect }
  | { readonly kind: 'read'; readonly read: CustomRead };

/**
 * A recorded observation, stored on a frame that rides into `playerView`. A number, a
 * boolean or a list of instance ids — identity-free by type — plus the one deliberate
 * exception: a list of card-name strings, which the `names` read only ever produces for a
 * card whose identity is already public to every player.
 */
export type CustomFact = number | boolean | readonly InstanceId[] | readonly string[];

/** What the interpreter sends back into a `yield`. */
export type CustomReply = string | CustomFact | undefined;

/** The generator a `custom()` id names. Synchronous — an async generator is refused. */
export type CustomGenerator = Generator<CustomYield, void, CustomReply>;

/** A registered escape-hatch ability. */
export type CustomAbility = (ctx: CustomContext) => CustomGenerator;

/** The return type of the `yield*` authoring helpers. */
export type CustomStep<R> = Generator<CustomYield, R, CustomReply>;

// ---------------------------------------------------------------------------
// The transcript — replay-to-resume's plain-data record
// ---------------------------------------------------------------------------

/**
 * One thing that has already happened in an in-flight `custom()` ability, in order. One
 * entry per yield the interpreter has carried out.
 */
export type CustomTranscriptStep =
  /** A decision that was answered, by its canonical token. */
  | { readonly kind: 'choice'; readonly token: string }
  /** An effect the interpreter already executed — replayed by *skipping* it. */
  | { readonly kind: 'effect' }
  /** A state observation, recorded so replay sees what the first run saw. */
  | { readonly kind: 'fact'; readonly value: CustomFact };

/**
 * The in-flight `custom()` ability on an `effect` frame (SPEC.md §4.3: "state stores the
 * in-flight ability's card + choices made"). The card is the frame's own `source`; this is
 * the generator id and everything it has done.
 */
export interface CustomRun {
  /** The registry id the frame's `custom` node named. */
  readonly id: string;
  /** Everything already carried out, oldest first. Plain data; identity-free. */
  transcript: CustomTranscriptStep[];
}

// ---------------------------------------------------------------------------
// Tokens
// ---------------------------------------------------------------------------

/** The reserved "none of them / stop here" token. */
export const DECLINE_TOKEN = 'none';

/** A card option's token: instance ids are public, so they key like every other in-play pick. */
export function cardToken(instance: InstanceId): string {
  return `i${instance}`;
}

/** The instance a `i<id>` token names, or `null` for anything else. */
export function tokenInstance(token: string): InstanceId | null {
  if (!/^i\d+$/.test(token)) return null;
  return Number(token.slice(1));
}

const TOKEN_SHAPE = /^[A-Za-z0-9][A-Za-z0-9_.-]*$/;
const DEF_ID_TOKEN = /^d\d+$/;

/**
 * The `n.` namespace: a **declared card name** token, for the "name a card" decision.
 *
 * Card names carry spaces, apostrophes and accents, none of which an action key may — so the
 * name is encoded reversibly into the token alphabet: letters and digits stand for
 * themselves, a space becomes `-`, and anything else becomes `.` + its UTF-16 code unit in
 * four hex digits. `nameToken('The Sorcerer's Hat')` is `n.The-Sorcerer.0027s-Hat`, and
 * `tokenCardName` reads the name back without touching any state — which is what keeps a
 * generator holding a decoded name a pure function of its transcript.
 */
const NAME_TOKEN_PREFIX = 'n.';
const NAME_ESCAPE = /^[0-9a-f]{4}$/;

/** The canonical action-key token for a declared card name. */
export function nameToken(name: string): string {
  let out = NAME_TOKEN_PREFIX;
  for (let i = 0; i < name.length; i += 1) {
    const char = name[i] as string;
    if (/[A-Za-z0-9]/.test(char)) out += char;
    else if (char === ' ') out += '-';
    else out += `.${name.charCodeAt(i).toString(16).padStart(4, '0')}`;
  }
  return out;
}

/** The card name a `n.…` token declares, or `null` for any other (or malformed) token. */
export function tokenCardName(token: string): string | null {
  if (!token.startsWith(NAME_TOKEN_PREFIX)) return null;
  const encoded = token.slice(NAME_TOKEN_PREFIX.length);
  let out = '';
  for (let i = 0; i < encoded.length; i += 1) {
    const char = encoded[i] as string;
    if (char === '-') out += ' ';
    else if (char === '.') {
      const hex = encoded.slice(i + 1, i + 5);
      if (!NAME_ESCAPE.test(hex)) return null;
      out += String.fromCharCode(Number.parseInt(hex, 16));
      i += 4;
    } else out += char;
  }
  return out;
}

/**
 * Reject a token that cannot be an action key, or that would carry a printed identity into a
 * frame. `d<defId>` is the engine's key for a hidden hand card (`play:d42`); recorded in a
 * transcript it would leak that card through `playerView`, so a `custom()` choice names cards
 * by instance instead.
 */
export function assertToken(token: string): void {
  if (!TOKEN_SHAPE.test(token)) {
    throw new CustomAbilityError(
      `"${token}" is not a usable custom() token (expected [A-Za-z0-9][A-Za-z0-9_.-]*)`,
    );
  }
  if (DEF_ID_TOKEN.test(token)) {
    throw new CustomAbilityError(
      `"${token}" keys a card by printed id; a custom() transcript rides into playerView, so ` +
        'a card choice must name an instance (SPEC.md §4.4)',
    );
  }
}

// ---------------------------------------------------------------------------
// The registry
// ---------------------------------------------------------------------------

const GeneratorFunction = Object.getPrototypeOf(function* () {}).constructor as new () => unknown;

const REGISTRY = new Map<string, CustomAbility>();

/**
 * Whether a *different* function for an already-registered id replaces it instead of
 * throwing. Off by default — see {@link setCustomHotReload}.
 */
let hotReloadTolerant = false;

/**
 * Opt in (or back out) of tolerating re-registration under a dev-server hot reload.
 *
 * The engine itself is bundler-agnostic and never reads `import.meta.hot` — the one caller
 * of this function is `apps/web`'s entry point, which knows whether it is running under Vite
 * dev. When a bundled card module is hot-swapped, its top-level `registerCustom` call reruns
 * and constructs a **new** generator function for the same id; without this, `registerCustom`
 * sees that as two different modules claiming one id and throws, which kills the reload.
 * Outside of dev (plain `node`, `vitest`, `vite build`), leave this off — a genuine id
 * collision between two cards should fail loudly, not be silently overwritten.
 */
export function setCustomHotReload(enabled: boolean): void {
  hotReloadTolerant = enabled;
}

/**
 * Register one escape-hatch ability. Card modules call this beside their `defineCard`.
 *
 * Registering the same id twice with a *different* function throws — two cards claiming one
 * id would silently swap behaviour depending on import order. Re-registering the identical
 * function is a no-op, so a module that is imported twice is fine. The throw is suppressed
 * under {@link setCustomHotReload} (dev-server hot reload only): the newer function replaces
 * the old one instead, which is what makes a card module's own edits show up on reload.
 */
export function registerCustom(id: string, ability: CustomAbility): void {
  if (id.length === 0) throw new CustomAbilityError('A custom() ability needs a non-empty id');
  if (!(ability instanceof GeneratorFunction)) {
    throw new CustomAbilityError(
      `custom("${id}") must be a synchronous generator function (SPEC.md §5.2)`,
    );
  }
  const existing = REGISTRY.get(id);
  if (existing !== undefined && existing !== ability && !hotReloadTolerant) {
    throw new CustomAbilityError(`custom("${id}") is already registered by another module`);
  }
  REGISTRY.set(id, ability);
}

/**
 * Register, replacing whatever was there. For tests and the scenario harness, which define a
 * generator inline per case; card modules use `registerCustom`.
 */
export function setCustom(id: string, ability: CustomAbility): void {
  REGISTRY.delete(id);
  registerCustom(id, ability);
}

export function unregisterCustom(id: string): void {
  REGISTRY.delete(id);
}

export function clearCustomRegistry(): void {
  REGISTRY.clear();
}

export function hasCustom(id: string): boolean {
  return REGISTRY.has(id);
}

/** Every registered id, sorted — the manifest input for ticket 14. */
export function customAbilityIds(): readonly string[] {
  return [...REGISTRY.keys()].sort();
}

/** The generator for an id, or a readable failure naming what is missing. */
export function requireCustom(id: string): CustomAbility {
  const ability = REGISTRY.get(id);
  if (ability === undefined) {
    throw new CustomAbilityError(
      `custom("${id}") is not registered; the card module must call registerCustom("${id}", …)`,
    );
  }
  return ability;
}

// ---------------------------------------------------------------------------
// Detecting the escape hatch (SPEC.md §5.3, ticket 14's coverage manifest)
// ---------------------------------------------------------------------------

/**
 * Every `custom()` id an IR value reaches, sorted and deduplicated. One plain-data walk, like
 * `cloneIr`, so a widened `Effect` union can never hide a `custom` node from the manifest.
 */
export function customIdsIn(value: unknown): readonly string[] {
  const found = new Set<string>();
  collectCustomIds(value, found);
  return [...found].sort();
}

function collectCustomIds(value: unknown, found: Set<string>): void {
  if (Array.isArray(value)) {
    for (const each of value) collectCustomIds(each, found);
    return;
  }
  if (value === null || typeof value !== 'object') return;
  const record = value as Record<string, unknown>;
  if (record['kind'] === 'custom' && typeof record['id'] === 'string') found.add(record['id']);
  for (const each of Object.values(record)) collectCustomIds(each, found);
}

/**
 * Whether a card's abilities reach for the escape hatch — `usesEscapeHatch` in the coverage
 * manifest (SPEC.md §5.2). Derived from the IR, never declared, so it cannot drift.
 */
export function usesEscapeHatch(abilities: readonly Ability[]): boolean {
  return customIdsIn(abilities).length > 0;
}

/** The `custom()` ids a card names that nothing has registered. Empty means the card is whole. */
export function missingCustomAbilities(abilities: readonly Ability[]): readonly string[] {
  return customIdsIn(abilities).filter((id) => !hasCustom(id));
}

// ---------------------------------------------------------------------------
// The authoring helpers — every yield, typed
// ---------------------------------------------------------------------------

/**
 * "Choose a card …". Returns the chosen instance, or `null` when the chooser declined (or
 * the pool was empty). The pool is the selector's, minus Ward — the same legality the `target`
 * node applies, so an illegal choice is never offered.
 */
export function* chooseCard(spec: {
  selector: Selector;
  as?: string;
  optional?: boolean;
  label?: string;
  chooser?: 'controller' | 'opponent';
}): CustomStep<InstanceId | null> {
  const reply = yield { kind: 'choose', spec: { kind: 'cards', ...spec } };
  if (typeof reply !== 'string') {
    throw new CustomAbilityError('A card choice was answered with something other than a token');
  }
  return reply === DECLINE_TOKEN ? null : tokenInstance(reply);
}

/**
 * A decision that is not about a card — a mode, a number, a yes/no. The tokens are the
 * generator's own dense ids, and the reply is the chosen one.
 */
export function* chooseOption(
  options: readonly CustomOptionSpec[],
  label?: string,
  chooser?: 'controller' | 'opponent',
): CustomStep<string> {
  const reply = yield {
    kind: 'choose',
    spec: {
      kind: 'options',
      options,
      ...(label === undefined ? {} : { label }),
      ...(chooser === undefined ? {} : { chooser }),
    },
  };
  if (typeof reply !== 'string') {
    throw new CustomAbilityError('An option choice was answered with something other than a token');
  }
  return reply;
}

/** Build a dense option list from labels: tokens `o0`, `o1`, … in written order. */
export function denseOptions(labels: readonly string[]): readonly CustomOptionSpec[] {
  return labels.map((label, index) => ({ token: `o${index}`, label }));
}

/** Hand IR effects to the interpreter, in order. Each runs fully before the next. */
export function* runEffect(...effects: readonly Effect[]): CustomStep<void> {
  for (const effect of effects) yield { kind: 'run', effect };
}

/** How many cards satisfy a selector. */
export function* readCount(of: Selector): CustomStep<number> {
  return numberFact(yield { kind: 'read', read: { kind: 'count', of } });
}

/** Any IR amount — "their {S}", "damage on that card", "for each …". */
export function* readAmount(amount: Amount): CustomStep<number> {
  return numberFact(yield { kind: 'read', read: { kind: 'amount', amount } });
}

/** Any IR condition — the general branch. */
export function* readTest(condition: Condition): CustomStep<boolean> {
  const reply = yield { kind: 'read', read: { kind: 'test', condition } };
  if (typeof reply !== 'boolean') throw new CustomAbilityError('A test read returned no boolean');
  return reply;
}

/** The instance ids satisfying a selector, in canonical pool order. */
export function* readPool(of: Selector): CustomStep<readonly InstanceId[]> {
  const reply = yield { kind: 'read', read: { kind: 'pool', of } };
  if (!Array.isArray(reply)) throw new CustomAbilityError('A pool read returned no id list');
  return reply as readonly InstanceId[];
}

/**
 * The printed name(s) of the card a reference names — the base name plus each name of a
 * multi-name card, so "the same name as" can be asked of the answer (`Selector.name` matches
 * the same list). `[]` when the reference names nothing. The card's identity must already be
 * public to every player — banished, in play, or revealed — or the read throws.
 */
export function* readNames(of: CardRef): CustomStep<readonly string[]> {
  const reply = yield { kind: 'read', read: { kind: 'names', of } };
  if (!Array.isArray(reply) || !reply.every((each) => typeof each === 'string')) {
    throw new CustomAbilityError('A names read returned no name list');
  }
  return reply as readonly string[];
}

/**
 * "Name a card" — the chooser commits to a card identity before anything is revealed, and the
 * declared **base name** comes back as a plain string to compare against a later `readNames`.
 * The option list is the catalog's whole declarable-name roster, so the guess is always a
 * legal LorcanaJSON card name and never free text — and never a version name, which is how
 * the official clarification is satisfied by construction.
 */
export function* chooseCardName(spec: {
  label?: string;
  chooser?: 'controller' | 'opponent';
} = {}): CustomStep<string> {
  const reply = yield { kind: 'choose', spec: { kind: 'name', ...spec } };
  if (typeof reply !== 'string') {
    throw new CustomAbilityError('A name choice was answered with something other than a token');
  }
  const name = tokenCardName(reply);
  if (name === null) {
    throw new CustomAbilityError(`"${reply}" is not a declared card name token`);
  }
  return name;
}

function numberFact(reply: CustomReply): number {
  if (typeof reply !== 'number') throw new CustomAbilityError('A numeric read returned no number');
  return reply;
}

// ---------------------------------------------------------------------------
// Driving one in-flight ability
// ---------------------------------------------------------------------------

function contextOf(frame: EffectFrame): AbilityContext {
  return {
    source: frame.source,
    controller: frame.controller,
    subject: frame.subject,
    eventSource: frame.eventSource,
    eventAmount: frame.eventAmount,
    eventDefender: frame.eventDefender,
    bindings: frame.bindings,
    playerBindings: frame.playerBindings,
  };
}

function customContextOf(frame: EffectFrame): CustomContext {
  return { source: frame.source, controller: frame.controller, subject: frame.subject };
}

/**
 * Run an in-flight `custom()` ability up to its next interaction with the world.
 *
 * Re-creates the generator, fast-forwards it through the frame's transcript, then carries out
 * the first uncovered yield: a decision blocks the frame, an effect is spliced into the
 * queue, and a read is answered and recorded on the spot. `node` is put back in front of the
 * queue whenever the ability has more to do, so the effect machine comes back to it.
 */
export function driveCustom(
  state: GameState,
  frame: EffectFrame,
  node: Extract<Effect, { kind: 'custom' }>,
): void {
  const run = beginOrResume(frame, node.id);
  const generator = requireCustom(run.id)(customContextOf(frame));
  let cursor = 0;
  let step = generator.next();

  for (let guard = 0; guard <= MAX_CUSTOM_STEPS; guard += 1) {
    if (step.done === true) {
      frame.custom = null;
      return;
    }
    const yielded = step.value;
    const recorded = run.transcript[cursor];
    if (recorded !== undefined) {
      cursor += 1;
      step = generator.next(replayReply(run.id, cursor, yielded, recorded));
      continue;
    }
    switch (yielded.kind) {
      case 'read': {
        const value = evalRead(state, contextOf(frame), yielded.read);
        run.transcript.push({ kind: 'fact', value });
        cursor += 1;
        step = generator.next(value);
        continue;
      }
      case 'run': {
        run.transcript.push({ kind: 'effect' });
        // The effect runs through the ordinary machine — including any choice *it* raises —
        // and the custom node behind it brings the generator back afterwards.
        frame.queue.unshift(yielded.effect, node);
        return;
      }
      case 'choose': {
        assertChoiceSpec(yielded.spec);
        frame.awaiting = { kind: 'custom-choice', spec: yielded.spec };
        frame.queue.unshift(node);
        return;
      }
    }
  }
  throw new CustomAbilityError(
    `custom("${run.id}") did not reach a decision within ${MAX_CUSTOM_STEPS} steps`,
  );
}

function beginOrResume(frame: EffectFrame, id: string): CustomRun {
  const existing = frame.custom;
  if (existing === null) {
    const fresh: CustomRun = { id, transcript: [] };
    frame.custom = fresh;
    return fresh;
  }
  if (existing.id !== id) {
    throw new CustomAbilityError(
      `custom("${id}") started while custom("${existing.id}") is still in flight; ` +
        'one escape hatch at a time (nest by yielding IR instead)',
    );
  }
  return existing;
}

/**
 * The reply a recorded transcript entry stands for. A yield that disagrees with what was
 * recorded means the generator is not a pure function of its transcript — exactly the bug
 * replay-to-resume exists to make impossible — so it fails loudly rather than diverging.
 */
function replayReply(
  id: string,
  position: number,
  yielded: CustomYield,
  recorded: CustomTranscriptStep,
): CustomReply {
  if (yielded.kind === 'choose' && recorded.kind === 'choice') return recorded.token;
  if (yielded.kind === 'run' && recorded.kind === 'effect') return undefined;
  if (yielded.kind === 'read' && recorded.kind === 'fact') return recorded.value;
  throw new CustomAbilityError(
    `custom("${id}") is not deterministic: replaying step ${position} recorded a ` +
      `"${recorded.kind}" but the generator yielded a "${yielded.kind}"`,
  );
}

function evalRead(state: GameState, ctx: AbilityContext, read: CustomRead): CustomFact {
  switch (read.kind) {
    case 'count':
      return selectorPool(state, ctx, read.of).length;
    case 'amount':
      return evalAmount(state, ctx, read.amount);
    case 'test':
      return evalCondition(state, ctx, read.condition);
    case 'pool':
      return selectorPool(state, ctx, read.of);
    case 'names': {
      const id = resolveCardRefIn(state, ctx, read.of);
      if (id === null) return [];
      const card = instanceAt(state, id);
      // The answer is recorded on a frame every view carries, so it may only ever name a card
      // the whole table already knows — reveal first, read second (SPEC.md §4.4).
      const everyone = allPlayersMask(state.players.length);
      if ((card.knownTo & everyone) !== everyone) {
        throw new CustomAbilityError(
          `custom() read the name of instance ${id}, whose identity is not public — ` +
            'reveal the card before reading its name (SPEC.md §4.4)',
        );
      }
      return cardNamesOf(requireDefinition(state.catalog, card.defId));
    }
  }
}

function assertChoiceSpec(spec: CustomChoiceSpec): void {
  if (spec.kind === 'cards') {
    if (spec.as !== undefined && spec.as.length === 0) {
      throw new CustomAbilityError('A custom() card choice binds under an empty id');
    }
    return;
  }
  // A name choice's options are the engine's own roster; there is nothing to validate here.
  if (spec.kind === 'name') return;
  if (spec.options.length === 0) {
    throw new CustomAbilityError('A custom() choice was raised with no options');
  }
  const seen = new Set<string>();
  for (const each of spec.options) {
    assertToken(each.token);
    if (seen.has(each.token)) {
      throw new CustomAbilityError(`A custom() choice offers "${each.token}" twice`);
    }
    seen.add(each.token);
  }
}

// ---------------------------------------------------------------------------
// The pending request, and answering it
// ---------------------------------------------------------------------------

/**
 * The `ChoiceRequest` a paused `custom()` ability raises. Rebuilt from the frame on demand —
 * the pool is evaluated against the state *now*, exactly as `targetPool` is for an ordinary
 * `target` node, which is what lets a resumed clone surface the same decision.
 */
export function customRequest(
  state: GameState,
  frame: EffectFrame,
  spec: CustomChoiceSpec,
): ChoiceRequest {
  const options: ChoiceOption[] = [];
  if (spec.kind === 'options') {
    for (const each of spec.options) {
      options.push(option({ kind: 'custom-choice', token: each.token }));
    }
  } else if (spec.kind === 'name') {
    // "Name a card": one option per declarable name, in the roster's canonical (sorted)
    // order. The roster is the catalog's, so the guess is always a real card name.
    const roster = declarableNames(state.catalog);
    if (roster === null || roster.length === 0) {
      throw new CustomAbilityError(
        'A custom() "name a card" choice needs a catalog that can enumerate card names',
      );
    }
    for (const name of roster) {
      options.push(option({ kind: 'custom-choice', token: nameToken(name) }));
    }
  } else {
    for (const id of customCardPool(state, frame, spec)) {
      options.push(option({ kind: 'custom-choice', token: cardToken(id) }));
    }
    // "Up to" includes none, and an empty pool must still leave the game a legal answer
    // ("resolves with no effect", CR §6.7.3).
    if (spec.optional === true || options.length === 0) {
      options.push(option({ kind: 'custom-choice', token: DECLINE_TOKEN }));
    }
  }
  return {
    kind: 'custom-choice',
    player: chooserOf(state, contextOf(frame), spec),
    options,
  };
}

/**
 * The cards a `cards` choice may pick: the selector's pool, minus Ward (CR §8.15).
 *
 * Ward asks about the **chooser**, not the ability's controller — the same rule `targetPool`
 * applies — which is what makes "each opponent chooses and discards a card" behave when the
 * decision belongs to somebody else.
 */
function customCardPool(
  state: GameState,
  frame: EffectFrame,
  spec: Extract<CustomChoiceSpec, { kind: 'cards' }>,
): readonly InstanceId[] {
  const ctx = contextOf(frame);
  const chooser = chooserOf(state, ctx, spec);
  return selectorPool(state, ctx, spec.selector).filter((id) =>
    canBeChosenBy(state, instanceAt(state, id), chooser),
  );
}

/**
 * Answer a pending `custom-choice`: record the token on the transcript and, for a bound card
 * choice, announce the pick exactly as a `target` node does (the Vanish substrate, CR §6.7.3).
 * The generator itself is not touched here — the effect machine re-drives it from the
 * transcript on the next step, which is the whole point.
 */
export function answerCustomChoice(state: GameState, frame: EffectFrame, token: string): void {
  const awaiting = frame.awaiting;
  if (awaiting === null || awaiting.kind !== 'custom-choice') {
    throw new CustomAbilityError('No custom() choice is pending');
  }
  const run = frame.custom;
  if (run === null) throw new CustomAbilityError('A custom() choice is pending with no ability');
  const legal = customRequest(state, frame, awaiting.spec).options.some(
    (each) => each.action.kind === 'custom-choice' && each.action.token === token,
  );
  if (!legal) {
    throw new CustomAbilityError(`"${token}" is not a legal answer to this custom() choice`);
  }
  frame.awaiting = null;
  run.transcript.push({ kind: 'choice', token });

  if (awaiting.spec.kind !== 'cards' || token === DECLINE_TOKEN) return;
  const chosen = tokenInstance(token);
  if (chosen === null) return;
  const card = instanceAt(state, chosen);
  emit(state, {
    type: 'card-chosen',
    player: frame.controller,
    instance: chosen,
    defId: card.defId,
    source: frame.source,
    ability: frame.label,
  });
  const bindAs = awaiting.spec.as;
  if (bindAs === undefined) return;
  if (frame.bindings.some((each) => each.id === bindAs)) {
    throw new CustomAbilityError(
      `custom("${run.id}") binds "${bindAs}" twice; a second choice needs its own id`,
    );
  }
  frame.bindings.push({ id: bindAs, instances: [chosen] });
}
